import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { useGetCompanyNewQuery } from "../../../../store/search/searchApiSlice";
import {
  useGetOptimisedCompanyDetailsQuery,
  useGetCompanyPurchaseDetailsMutation,
} from "../../../../store/Company/companyApiSlice";
import { formatDateToISO } from "../../../../utils/Helper";

/* Reading the company behind /search/:id. */

/* Transcribed: the viewer has claimed this company if it appears in their
   individual's request list. */
export const hasClaimed = (userInfo, id) => {
  const match = userInfo?.individual?.[0]?.requestIndividual?.find(
    (i) => i?.companyID?.companyID === id
  );
  return match?.companyID?.companyID === id;
};

/* Transcribed: the viewer's role on this company, from their individual's
   company list. */
export const roleOn = (userInfo, id) => {
  const companies = userInfo?.individual?.[0]?.individual || [];
  return companies.find((c) => c?.companyID?.companyID === id)?.role;
};

/* Transcribed verbatim, including that it compares whole days: today must fall
   between the purchase and expiry dates inclusive. */
export const purchaseWindowValid = (purchase) => {
  const currentDate = new Date(formatDateToISO(new Date()));
  const start = new Date(purchase?.purchaseDate);
  const end = new Date(purchase?.expireDate);
  return currentDate >= start && currentDate <= end;
};

/* Transcribed: an enterprise purchase is reshaped onto the same fields a
   personal purchase carries, so the window check reads one shape. */
export const normalisePurchase = (res) => {
  const enterprise = res?.data?.enterprise;
  if (enterprise?.purchaseID) {
    return {
      purchaseID: enterprise.purchaseID,
      companyID: enterprise.companyID,
      planID: enterprise.planID,
      purchaseDate: enterprise.purchaseDate,
      expireDate: enterprise.expireDate,
      purchaseType: enterprise.purchaseType,
      wb_id: enterprise.wb_id,
      userID: enterprise.userID,
    };
  }
  return res?.data?.purchase;
};

export const useCompanyProfile = () => {
  const { id } = useParams();
  const { token, userInfo } = useSelector((state) => state.auth);
  const { worldBoxToken } = useSelector((state) => state.search);

  const [purchase, setPurchase] = useState("");
  const [getPurchaseDetails] = useGetCompanyPurchaseDetailsMutation();

  const trevexId = String(id || "").includes("TRX");
  const isClaimed = useMemo(() => hasClaimed(userInfo, id), [userInfo, id]);
  const role = useMemo(() => roleOn(userInfo, id), [userInfo, id]);

  /* Re-runs the purchase read on demand. */
  const [purchaseTrigger, setPurchaseTrigger] = useState(false);
  const refreshPurchase = useCallback(() => setPurchaseTrigger((t) => !t), []);

  /* Transcribed: asked for on every id or session change, and the result is
     whatever comes back — the live effect stores it unconditionally. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getPurchaseDetails({
        companyID: id,
        userID: userInfo?.individual?.[0]?.userID,
        token,
      });
      if (!cancelled) setPurchase(normalisePurchase(res));
    })().catch(() => {
      /* The live effect has no catch, so a rejection here becomes an unhandled promise. */
    });
    return () => {
      cancelled = true;
    };
  }, [id, userInfo, token, getPurchaseDetails, purchaseTrigger]);

  const purchaseSubScriptionValid = useMemo(
    () => purchaseWindowValid(purchase),
    [purchase]
  );

  const {
    data: trevexData,
    isLoading: trevexLoading,
    isSuccess: gotTrevex,
    isError,
    error,
  } = useGetOptimisedCompanyDetailsQuery(
    { isClaimed, role, purchaseSubScriptionValid, companyId: id, token },
    { skip: !trevexId }
  );

  const {
    data: worldBoxData,
    isLoading: wbLoading,
    isSuccess: gotWorldBox,
  } = useGetCompanyNewQuery({ id, worldBoxToken }, { skip: trevexId });

  const company = trevexId
    ? gotTrevex
      ? trevexData
      : undefined
    : gotWorldBox
    ? worldBoxData
    : undefined;

  return {
    id,
    company,
    trevexId,
    role,
    isClaimed,
    purchaseSubScriptionValid,
    refreshPurchase,
    isLoading: trevexId ? trevexLoading : wbLoading,
    /* Transcribed: the live screen treats this one error as a dead session. */
    isSessionExpired: Boolean(isError && error?.data?.code === "token_not_valid"),
  };
};

export default useCompanyProfile;
